import { ref, computed } from "vue";
import { defineStore } from "pinia";
import { useCoins } from "@/composables/useCoins";
import { appendUnique } from "@/utils/history/appendUnique.js";

const secondsInDay = 86400;

export const DEPOSIT_TYPES = {
  FIX: "fix",
  FLEX: "flex",
  FULLFLEX: "fullflex",
};
export const DEPOSIT_STATUS = {
  OPEN: "open",
  CLOSE: "close",
  CLOSING: "closing",
  PENDING: "pending",
};
export const CAPITALIZATION_POLICY = {
  OPTIONAL: "optional",
  REQUIRED: "required",
  DISABLED: "disabled",
};
export const HISTORY_LIMIT = 10;
export const AVAILABLE_DAYS_FOR_CLOSING = 6;

export const useCoinholdStore = defineStore("coinhold", () => {
  const balances = ref(null);
  const setBalance = (data) => {
    balances.value = data;
  };

  const history = ref(null);
  // append — следующая порция истории (offset > 0)
  const setHistory = (data, { append = false } = {}) => {
    const items = data.operations ?? [];
    history.value = {
      total: data.total_count,
      items:
        append && history.value
          ? appendUnique(history.value.items, items)
          : items,
    };
  };

  const historyLimit = HISTORY_LIMIT;

  const currentCoinhold = ref([]);
  const setCurrentCoinhold = (item) => {
    const coins = useCoins().configCoins.value;
    const coin = coins.find((c) => c.id === item.setting.coin.toLowerCase());
    item.coin = coin;
    item.days = item.setting.open_duration / secondsInDay;
    item.is_reducible =
      item.setting.close_enable && item.setting.withdraw_enable;
    item.type = DEPOSIT_TYPES.FULLFLEX;
    if (item.endDate && item.setting.withdraw_enable) {
      item.type = DEPOSIT_TYPES.FLEX;
    } else if (item.endDate && !item.setting.withdraw_enable) {
      item.type = DEPOSIT_TYPES.FIX;
    }

    currentCoinhold.value = item;
  };

  const list = ref([]);
  const setList = (data) => {
    const coins = useCoins().configCoins.value;
    let arr = data.deposits ? data.deposits : [];
    arr = arr.map((a) => {
      const coin = coins.find((c) => c.id === a.setting.coin.toLowerCase());
      a.coin = coin;
      a.days = a.setting.open_duration / secondsInDay;
      return a;
    });
    list.value = arr;
  };

  const coinholdTypes = ref([]);

  // /wallets-staking/coin-max-rates: [{ coin: "USDT", percent: 14 }, …]
  const coinMaxRates = ref([]);
  const setCoinMaxRates = (rates) => {
    coinMaxRates.value = Array.isArray(rates) ? rates : [];
  };
  const getCoinMaxRate = (coinId) =>
    coinMaxRates.value.find((item) => item.coin === coinId?.toUpperCase())
      ?.percent ?? 0;
  const setTypes = (data) => {
    const coins = useCoins().configCoins.value;
    const types = data.settings
      .map((a) => {
        const days = a.open_duration / secondsInDay;
        return { ...a, days };
      })
      .sort((a, b) => Number(b.percent) - Number(a.percent));
    coinholdTypes.value = coins
      .filter((a) => types.find((b) => b.coin.toLowerCase() === a.id))
      .map((a) => ({
        ...a,
        types: types.filter((b) => b.coin.toLowerCase() === a.id),
      }))
      .map((a) => {
        a.maxPercent = Math.max(...a.types.map((b) => Number(b.percent)));
        return a;
      });
    form.value.coin = coinholdTypes.value[0];
    form.value.type = form.value.coin.types.find(
      (type) =>
        type.days === form.value.period_days &&
        type.capitalization_enabled === form.value.isCapitalized,
    );
    form.value.coin.types = types.filter(
      (a) => a.coin.toLowerCase() === form.value.coin.id,
    );
  };

  const form = ref({
    coin: null,
    period_days: 360,
    type: null,
    isCapitalized: true,
    mining_percent: 100,
    amount: null,
    prolongation: null,
  });

  const setPeriod = (period) => {
    if (period > 360) period = 360;
    form.value.period_days = period;
    form.value.type = form.value.coin.types.find(
      (a) => a.days === form.value.period_days,
    );
    form.value.isCapitalized = form.value.type?.capitalization_enabled;
  };

  const setType = (type) => {
    form.value.type = type;
    form.value.isCapitalized = form.value.type?.capitalization_enabled;
  };

  const setAmount = (amount) => {
    form.value.amount = amount;
  };

  const setCapitalized = (value) => {
    form.value.isCapitalized = value;
  };

  const setMiningPercent = (value) => {
    form.value.mining_percent = value;
  };

  const setCoin = (coin) => {
    const coinObj = coinholdTypes.value.find((a) => a.id === coin);
    if (coinObj) {
      form.value.amount = null;
      form.value.coin = coinObj;
      form.value.type = form.value.coin.types.find(
        (a) => a.days === form.value.period_days,
      );
    }
  };

  const formClear = () => {
    form.value.amount = null;
    form.value.period_days = 360;
    form.value.isCapitalized = true;
    form.value.mining_percent = 100;
    setCoin(coinholdTypes.value[0].id);
  };

  // Автопополнение канала (mining/wallet) включено на ДРУГОМ депозите той же монеты.
  // Источник правды — list; exceptId исключает текущий депозит (его редактируем, не блокируем).
  const isMiningTakenElsewhere = (coin, exceptId) =>
    list.value.some(
      (d) =>
        d.id !== exceptId &&
        d.setting.coin === coin &&
        Number(d.mining_auto_top_up_percent) > 0,
    );

  const isWalletTakenElsewhere = (coin, exceptId) =>
    list.value.some(
      (d) =>
        d.id !== exceptId &&
        d.setting.coin === coin &&
        d.wallet_auto_top_up_enabled === true,
    );

  const historyFilters = ref({
    coin: null,
    finished_after: null,
    finished_before: null,
    operation_type: null,
  });

  const setHistoryFilters = (filters = {}) => {
    historyFilters.value = {
      ...historyFilters.value,
      ...filters,
    };
  };

  const resetFilters = () => {
    historyFilters.value.coin = null;
    historyFilters.value.finished_after = null;
    historyFilters.value.finished_before = null;
    historyFilters.value.operation_type = null;
  };

  const isNoFilters = computed(() => {
    return (
      !historyFilters.value.coin &&
      !historyFilters.value.finished_after &&
      !historyFilters.value.finished_before &&
      !historyFilters.value.operation_type
    );
  });

  const status = ref({
    success: true,
    title: null,
    description: null,
    options: [], // [{title, content}]
    buttons: [], // [{title, route, type}]
  });

  const updateStatus = (value) => {
    status.value.success = value.success;
    status.value.title = value.title;
    status.value.description = value.description;
    status.value.options = value.options;
    status.value.buttons = value.buttons;
  };

  const resetStatus = () => {
    status.value.success = true;
    status.value.title = null;
    status.value.description = null;
    status.value.options = [];
    status.value.buttons = [];
  };

  return {
    balances,
    setBalance,
    list,
    setList,
    coinholdTypes,
    setTypes,
    coinMaxRates,
    setCoinMaxRates,
    getCoinMaxRate,
    form,
    setPeriod,
    setType,
    setAmount,
    setCapitalized,
    setCoin,
    formClear,
    setMiningPercent,
    currentCoinhold,
    setCurrentCoinhold,
    isMiningTakenElsewhere,
    isWalletTakenElsewhere,
    history,
    setHistory,
    historyLimit,
    historyFilters,
    setHistoryFilters,
    resetFilters,
    isNoFilters,
    status,
    updateStatus,
    resetStatus,
  };
});
